/**
 * LottieOverlay — wrapper rundt lottie-web for premium-animasjoner.
 *
 * Bygd i runde 43 for å levere ekte vektor-animasjoner som komplement til
 * Phaser-partiklene. Lottie-filer ligger i `public/assets/lottie/` og
 * lastes lazily første gang de brukes.
 *
 * Strategi:
 * - DOM-overlay (ikke Phaser-render). Lottie tegner via SVG/canvas inn i
 *   en `<div>` posisjonert absolutt i `#sp-canvas-host`.
 * - Phaser-koordinater (1080×1920) skaleres til DOM-piksler.
 * - JSON-cache: hver animasjon hentes én gang, så reused fra minne.
 * - Auto-cleanup: animasjonen fjerner seg selv etter `complete`-event,
 *   eller etter manuell `destroy()`-kall.
 * - Test-trygg: hvis `lottie-web` ikke kan importeres (jsdom) eller
 *   `document` mangler, blir alle metoder no-ops som returnerer null.
 *
 * Plassholder-filer ligger i public/assets/lottie/. Sven kan erstatte
 * med skikkelige Lottie-filer fra lottiefiles.com når han ønsker mer
 * polert visuell payoff.
 */

import type Phaser from 'phaser';

export type LottieNavn =
  | 'tier-up-confetti'
  | 'brage-trophy'
  | 'loading-books';

export interface LottieValg {
  /** Bredde i Phaser-koordinater (1080×1920). Default: 400. */
  width?: number;
  /** Høyde i Phaser-koordinater. Default: 400. */
  height?: number;
  /** Skal animasjonen loope? Default: false. */
  loop?: boolean;
  /** Hvor mange ganger spille (kun hvis loop=false). Default: 1. */
  spillinger?: number;
  /** Callback når animasjonen er ferdig. */
  onFerdig?: () => void;
  /** Z-index i DOM. Default: 950 (over de fleste modaler). */
  zIndex?: number;
  /** Auto-fjern fra DOM når ferdig. Default: true. */
  autoFjern?: boolean;
  /** Origin (0..1) for posisjonering. Default: { x: 0.5, y: 0.5 } (sentrert). */
  origin?: { x: number; y: number };
}

const STYLE_ID = 'sp-lottie-style';
const CONTAINER_CLASS = 'sp-lottie-overlay';

const CSS = `
.${CONTAINER_CLASS} {
  position: absolute;
  pointer-events: none;
  z-index: 950;
}
.${CONTAINER_CLASS} svg {
  width: 100%;
  height: 100%;
}
`;

type LottieAnim = {
  destroy(): void;
  addEventListener(name: string, handler: () => void): void;
  play(): void;
  stop(): void;
  pause(): void;
  goToAndStop(value: number, isFrame?: boolean): void;
};

type LottieBibliotek = {
  loadAnimation(params: {
    container: HTMLElement;
    renderer: 'svg' | 'canvas' | 'html';
    loop: boolean | number;
    autoplay: boolean;
    animationData: unknown;
  }): LottieAnim;
};

/**
 * Instans returnert ved spillstart. Bruk `destroy()` for å avbryte
 * tidlig (f.eks. når modal lukkes før animasjonen er ferdig).
 */
export interface LottieHandle {
  destroy(): void;
  readonly element: HTMLDivElement;
  readonly ferdig: Promise<void>;
}

export class LottieOverlay {
  private jsonCache: Map<LottieNavn, unknown> = new Map();
  private aktiveAnimasjoner: Set<LottieAnim> = new Set();
  private aktiveContainere: Set<HTMLDivElement> = new Set();
  private bibliotek?: LottieBibliotek;
  private bibliotekLastFeilet = false;
  private bibliotekLoadPromise?: Promise<LottieBibliotek | null>;

  /** True hvis Lottie-biblioteket og DOM er tilgjengelig. */
  erTilgjengelig(): boolean {
    return typeof document !== 'undefined' && !this.bibliotekLastFeilet;
  }

  /**
   * Pre-last en JSON-fil slik at første visning er instant.
   * Trygt å kalle flere ganger.
   */
  async forhandslast(navn: LottieNavn): Promise<void> {
    if (this.jsonCache.has(navn)) return;
    try {
      const json = await this.lastJson(navn);
      this.jsonCache.set(navn, json);
    } catch (err) {
      console.warn(`Klarte ikke laste Lottie-fil ${navn}`, err);
    }
  }

  /**
   * Spill av en Lottie-animasjon i Phaser-koordinater.
   *
   * @param scene Phaser-scene (brukes kun for å lese canvas-bounds).
   * @param x X i Phaser-koordinater.
   * @param y Y i Phaser-koordinater.
   * @param navn Animasjonsnavn (uten .json-suffix).
   * @param valg Valgfri konfigurasjon.
   */
  spill(
    scene: Phaser.Scene | undefined,
    x: number,
    y: number,
    navn: LottieNavn,
    valg: LottieValg = {},
  ): LottieHandle | null {
    void scene;
    if (typeof document === 'undefined') return null;

    const width = valg.width ?? 400;
    const height = valg.height ?? 400;
    const origin = valg.origin ?? { x: 0.5, y: 0.5 };
    const loop = valg.loop ?? false;
    const spillinger = valg.spillinger ?? 1;
    const zIndex = valg.zIndex ?? 950;
    const autoFjern = valg.autoFjern ?? true;

    this.sikreStyle();

    const host = document.getElementById('sp-canvas-host') ?? document.body;
    const container = document.createElement('div');
    container.className = CONTAINER_CLASS;
    container.style.zIndex = String(zIndex);
    container.setAttribute('aria-hidden', 'true');
    host.appendChild(container);
    this.aktiveContainere.add(container);

    this.posisjonerContainer(container, x, y, width, height, origin);

    let ferdigResolve: () => void = () => undefined;
    const ferdig = new Promise<void>((resolve) => {
      ferdigResolve = resolve;
    });

    const fjern = () => {
      if (!this.aktiveContainere.has(container)) return;
      this.aktiveContainere.delete(container);
      if (container.parentNode) container.parentNode.removeChild(container);
      ferdigResolve();
      valg.onFerdig?.();
    };

    const handle: LottieHandle = {
      element: container,
      ferdig,
      destroy: () => fjern(),
    };

    void this.hentBibliotek().then((bib) => {
      if (!bib || !this.aktiveContainere.has(container)) {
        // Fallback: vis tomt container i 600ms slik at onFerdig fortsatt fyrer
        window.setTimeout(() => fjern(), 600);
        return;
      }
      const json = this.jsonCache.get(navn);
      if (json) {
        this.startAnimasjon(bib, container, json, loop, spillinger, autoFjern, fjern);
        return;
      }
      void this.lastJson(navn)
        .then((data) => {
          this.jsonCache.set(navn, data);
          if (!this.aktiveContainere.has(container)) return;
          this.startAnimasjon(bib, container, data, loop, spillinger, autoFjern, fjern);
        })
        .catch((err) => {
          console.warn(`Lottie-fil ${navn} kunne ikke lastes`, err);
          window.setTimeout(() => fjern(), 600);
        });
    });

    return handle;
  }

  /** Stopp og fjern alle aktive animasjoner. */
  stoppAlle(): void {
    for (const anim of this.aktiveAnimasjoner) {
      try {
        anim.destroy();
      } catch {
        // ignore
      }
    }
    this.aktiveAnimasjoner.clear();
    for (const c of this.aktiveContainere) {
      if (c.parentNode) c.parentNode.removeChild(c);
    }
    this.aktiveContainere.clear();
  }

  /** For testing — antall aktive containere. */
  get aktiveAntall(): number {
    return this.aktiveContainere.size;
  }

  private startAnimasjon(
    bib: LottieBibliotek,
    container: HTMLDivElement,
    json: unknown,
    loop: boolean,
    spillinger: number,
    autoFjern: boolean,
    onFerdig: () => void,
  ): void {
    try {
      const anim = bib.loadAnimation({
        container,
        renderer: 'svg',
        loop: loop ? true : Math.max(0, spillinger - 1),
        autoplay: true,
        animationData: json,
      });
      this.aktiveAnimasjoner.add(anim);
      if (!loop && autoFjern) {
        anim.addEventListener('complete', () => {
          this.aktiveAnimasjoner.delete(anim);
          try {
            anim.destroy();
          } catch {
            // ignore
          }
          onFerdig();
        });
      }
    } catch (err) {
      console.warn('Klarte ikke starte Lottie-animasjon', err);
      onFerdig();
    }
  }

  private async hentBibliotek(): Promise<LottieBibliotek | null> {
    if (this.bibliotek) return this.bibliotek;
    if (this.bibliotekLastFeilet) return null;
    if (this.bibliotekLoadPromise) return this.bibliotekLoadPromise;

    this.bibliotekLoadPromise = (async () => {
      try {
        const mod = (await import('lottie-web')) as unknown as {
          default?: LottieBibliotek;
        } & LottieBibliotek;
        const lib = (mod.default ?? mod) as LottieBibliotek;
        if (!lib || typeof lib.loadAnimation !== 'function') {
          this.bibliotekLastFeilet = true;
          return null;
        }
        this.bibliotek = lib;
        return lib;
      } catch (err) {
        console.warn('Lottie-web kunne ikke lastes', err);
        this.bibliotekLastFeilet = true;
        return null;
      }
    })();

    return this.bibliotekLoadPromise;
  }

  private async lastJson(navn: LottieNavn): Promise<unknown> {
    const url = `assets/lottie/${navn}.json`;
    if (typeof fetch === 'undefined') {
      throw new Error('fetch ikke tilgjengelig');
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    return res.json();
  }

  private posisjonerContainer(
    container: HTMLDivElement,
    x: number,
    y: number,
    width: number,
    height: number,
    origin: { x: number; y: number },
  ): void {
    const host = document.getElementById('sp-canvas-host');
    if (!host) {
      container.style.left = `${x}px`;
      container.style.top = `${y}px`;
      container.style.width = `${width}px`;
      container.style.height = `${height}px`;
      return;
    }
    const hostW = host.clientWidth || 1080;
    const hostH = host.clientHeight || 1920;
    const scaleX = hostW / 1080;
    const scaleY = hostH / 1920;
    const left = Math.round((x - width * origin.x) * scaleX);
    const top = Math.round((y - height * origin.y) * scaleY);
    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
    container.style.width = `${Math.round(width * scaleX)}px`;
    container.style.height = `${Math.round(height * scaleY)}px`;
  }

  private sikreStyle(): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
}

export const lottieOverlay = new LottieOverlay();
